import { ReactNode } from 'react';
import { ReactComponent as CalendarImage } from 'assets/icons/calendar.svg';
import { ReactComponent as MicroImage } from 'assets/icons/micro.svg';
import { ReactComponent as CheckedListImage } from 'assets/icons/checked-list.svg';
import { ReactComponent as RewardImage } from 'assets/icons/reward.svg';

import { AllStatuses, ItemOneOf } from 'interfaces';
import { getTasks } from 'api/task.service';
import { getBroadcasts } from 'api/broadcasts.service';
import { getRecognitions } from 'api/recognitions.service';
import { taskKeys } from 'hooks/api/tasks/queryKeys';
import { broadcastKeys } from 'hooks/api/broadcasts/queryKeys';
import { recognitionKeys } from 'hooks/api/recognitions/queryKeys';
import { Broadcast } from 'models/broadcast.model';
import { broadcastRoutes } from 'router/broadcastRoutes';
import { recognitionRoutes } from 'router/recognitionRoutes';
import { taskRoutes } from 'router/taskRoutes';

type WidgetItem = { id: string; title: string; date?: string };

type WidgetQuery = {
  queryKey: readonly unknown[];
  queryFn: () => Promise<ItemOneOf[]>;
  sortAndMap: (items: ItemOneOf[]) => WidgetItem[];
};

interface HomeData {
  title: string;
  tooltip: string;
  icon: ReactNode;
  navigateLink: (id: string) => string;
  items: WidgetItem[];
}

interface WidgetStaticConfig extends Omit<HomeData, 'items'> {
  query: WidgetQuery;
}

const STAR = '★';

const favoriteStarIcon = (
  <span style={{ color: 'var(--uui-warning-50)' }}>{STAR}</span>
);

const getFavoriteWidgetDetails = (entity: 'task' | 'broadcast') => ({
  title: entity === 'task' ? 'Favourite Tasks' : 'Favourite Broadcasts',
  tooltip:
    entity === 'task' ? 'Your favourite tasks' : 'Your favourite broadcasts',
  icon: favoriteStarIcon,
});

const sortByUpdatedAt = (items: ItemOneOf[]): WidgetItem[] =>
  [...items]
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
    .map((item) => ({ id: item.id, title: item.title, date: item.updatedAt }));

const widgetsConfig: Record<string, WidgetStaticConfig> = {
  'scheduled-broadcasts': {
    title: 'Scheduled Broadcasts',
    tooltip:
      'The list of approved broadcasts that will be sent to all users soon',
    icon: <CalendarImage />,
    navigateLink: broadcastRoutes.broadcastId,
    query: {
      queryKey: broadcastKeys.broadcastByStatus({
        status: AllStatuses.APPROVED,
      }),
      queryFn: () =>
        getBroadcasts({ status: AllStatuses.APPROVED }) as Promise<ItemOneOf[]>,
      sortAndMap: (items) =>
        [...(items as Broadcast[])]
          .sort((a, b) => {
            if (!a.schedule || !b.schedule) return 0;

            return (
              new Date(a.schedule).getTime() - new Date(b.schedule).getTime()
            );
          })
          .map((item) => ({
            id: item.id,
            title: item.title,
            date: item.schedule?.toString(),
          })),
    },
  },
  'pending-broadcasts': {
    title: 'Pending Broadcasts',
    tooltip: 'The list of Broadcasts awaiting your approval',
    icon: <MicroImage />,
    navigateLink: broadcastRoutes.broadcastId,
    query: {
      queryKey: broadcastKeys.broadcastByStatus({
        status: AllStatuses.PENDING_APPROVAL,
        isOwner: false,
      }),
      queryFn: () =>
        getBroadcasts({
          status: AllStatuses.PENDING_APPROVAL,
          isOwner: false,
        }) as Promise<ItemOneOf[]>,
      sortAndMap: sortByUpdatedAt,
    },
  },
  'my-broadcasts': {
    title: 'My Broadcasts',
    tooltip: 'The list of Broadcasts created or updated by you',
    icon: <MicroImage />,
    navigateLink: broadcastRoutes.broadcastId,
    query: {
      queryKey: broadcastKeys.broadcastByStatus({
        status: AllStatuses.DRAFT,
        isOwner: true,
      }),
      queryFn: () =>
        getBroadcasts({ status: AllStatuses.DRAFT, isOwner: true }) as Promise<
          ItemOneOf[]
        >,
      sortAndMap: sortByUpdatedAt,
    },
  },
  'favourite-broadcasts': {
    ...getFavoriteWidgetDetails('broadcast'),
    navigateLink: broadcastRoutes.broadcastId,
    query: {
      queryKey: broadcastKeys.favourites,
      queryFn: () =>
        getBroadcasts({ favourites: true }) as Promise<ItemOneOf[]>,
      sortAndMap: sortByUpdatedAt,
    },
  },
  'pending-tasks': {
    title: 'Pending Tasks',
    tooltip: 'The list of Tasks awaiting your approval',
    icon: <CheckedListImage />,
    navigateLink: taskRoutes.taskId,
    query: {
      queryKey: taskKeys.taskByStatus({
        status: AllStatuses.PENDING_APPROVAL,
        isOwner: false,
      }),
      queryFn: () =>
        getTasks({
          status: AllStatuses.PENDING_APPROVAL,
          isOwner: false,
        }) as Promise<ItemOneOf[]>,
      sortAndMap: sortByUpdatedAt,
    },
  },
  'my-tasks': {
    title: 'My Tasks',
    tooltip: 'The list of Tasks created or updated by you',
    icon: <CheckedListImage />,
    navigateLink: taskRoutes.taskId,
    query: {
      queryKey: taskKeys.taskByStatus({
        status: AllStatuses.DRAFT,
        isOwner: true,
      }),
      queryFn: () =>
        getTasks({ status: AllStatuses.DRAFT, isOwner: true }) as Promise<
          ItemOneOf[]
        >,
      sortAndMap: sortByUpdatedAt,
    },
  },
  'favourite-tasks': {
    ...getFavoriteWidgetDetails('task'),
    navigateLink: taskRoutes.taskId,
    query: {
      queryKey: taskKeys.favourites,
      queryFn: () => getTasks({ favourites: true }) as Promise<ItemOneOf[]>,
      sortAndMap: sortByUpdatedAt,
    },
  },
  'pending-recognitions': {
    title: 'Pending Recognitions',
    tooltip: 'The list of Recognitions awaiting your approval',
    icon: <RewardImage />,
    navigateLink: recognitionRoutes.recognitionId,
    query: {
      queryKey: recognitionKeys.recognitionByStatus({
        status: AllStatuses.PENDING_APPROVAL,
        isOwner: false,
      }),
      queryFn: () =>
        getRecognitions({
          status: AllStatuses.PENDING_APPROVAL,
          isOwner: false,
        }) as Promise<ItemOneOf[]>,
      sortAndMap: sortByUpdatedAt,
    },
  },
  'my-recognitions': {
    title: 'My Recognitions',
    tooltip: 'The list of Recognitions created or updated by you',
    icon: <RewardImage />,
    navigateLink: recognitionRoutes.recognitionId,
    query: {
      queryKey: recognitionKeys.recognitionByStatus({
        status: AllStatuses.DRAFT,
        isOwner: true,
      }),
      queryFn: () =>
        getRecognitions({
          status: AllStatuses.DRAFT,
          isOwner: true,
        }) as Promise<ItemOneOf[]>,
      sortAndMap: sortByUpdatedAt,
    },
  },
};

const DEFAULT_WIDGETS = [
  'scheduled-broadcasts',
  'my-broadcasts',
  'my-tasks',
  'my-recognitions',
];

export type { HomeData, WidgetItem, WidgetQuery, WidgetStaticConfig };
export { DEFAULT_WIDGETS, widgetsConfig };
